import { Divider, Radio } from 'antd';
import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import Modifier from './Modifier';
import { modifierContext } from '../ModifierProvider';
import OptionRow from '../OptionRow';
import { getActiveModifierOptions, getModifierMinSelections, isAutoApplied } from '../utils';

function SingleSelectModifier({ modifier, disabled }) {
  const { isSelected, handleChange, selectedModifiers } = useContext(modifierContext);
  const activeOptions = useMemo(() => getActiveModifierOptions(modifier), [modifier]);

  useEffect(() => {
    const first = activeOptions[0];
    if (!isAutoApplied(modifier) || !first || activeOptions.some((option) => isSelected({ modifier, option }))) {
      return;
    }
    handleChange({ modifier, option: first, value: { ...first, qty: 1 } });
  }, [modifier, activeOptions, handleChange, isSelected]);

  const selectedOption = useMemo(() => {
    let selectedId = null;
    activeOptions.forEach((option) => {
      if (isSelected({ option, modifier })) {
        selectedId = option.id;
      }
    });
    return selectedId;
  }, [activeOptions, selectedModifiers, modifier, isSelected]);

  return (
    <Radio.Group
      style={{ width: '100%' }}
      name={modifier.id}
      value={selectedOption}
    >
      {activeOptions.map((option, index) => (
        <ModifierOption key={option.id} modifier={modifier} option={option} disabled={disabled} showDivider={index !== activeOptions.length - 1} />
      ))}
    </Radio.Group>
  );
}
function ModifierOption({
  modifier, disabled, option, showDivider,
}) {
  const {
    isSelected, handleChange, getNestedOptions, hasSelectedNestedOptions,
  } = useContext(modifierContext);
  // Auto-expand if this option has selected nested options (e.g., when editing)
  const [showChildren, setShowChildren] = useState(false);
  const checked = isSelected({ option, modifier });
  const nestedModifiers = useMemo(() => getNestedOptions(option), [getNestedOptions, option]);

  // Auto-expand nested modifiers when editing if they have selected options
  useEffect(() => {
    if (hasSelectedNestedOptions(option)) {
      setShowChildren(true);
    }
  }, [option, hasSelectedNestedOptions]);

  return (
    <div key={option.id}>
      <Radio
        value={option.id}
        checked={checked}
        disabled={disabled}
        className="omc-select-row"
        onClick={() => {
          setShowChildren(!showChildren);
          if (checked && getModifierMinSelections(modifier) === 0) {
            return handleChange({
              modifier,
              option,
              value: undefined,
            });
          }
          handleChange({
            modifier,
            option,
            value: {
              ...option,
              qty: 1,
            },
          });
        }}
      >
        <OptionRow option={option} showChildren={showChildren} />
      </Radio>
      {showChildren && nestedModifiers?.length > 0 && nestedModifiers.map((m, index) => (
        <Modifier modifier={m} key={m.id ?? index} isNested />
      ))}
      {showDivider && <Divider className="my-3" />}
    </div>
  );
}

export default SingleSelectModifier;
